// src/pages/Users/Inscription/dossier/components/UsefulInformationPanel.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBookOpen,
  FaChevronDown,
  FaDownload,
  FaFileAlt,
  FaInfoCircle,
} from "react-icons/fa";

import { toAbsoluteUrl } from "../utils/dossierInscription.utils";

export default function UsefulInformationPanel({
  segments = [],
}) {
  const [openId, setOpenId] =
    useState(null);

  /*
   * Le premier bloc est volontairement ouvert :
   * l'utilisateur doit réellement voir
   * les informations.
   */
  useEffect(() => {
    if (
      openId === null &&
      segments.length
    ) {
      setOpenId(
        segments[0].id,
      );
    }
  }, [segments, openId]);

  const totalFiles =
    useMemo(
      () =>
        segments.reduce(
          (total, segment) =>
            total +
            (Array.isArray(
              segment?.fichiers,
            )
              ? segment.fichiers
                  .length
              : 0),
          0,
        ),
      [segments],
    );

  if (!segments.length) {
    return null;
  }

  return (
    <section
      className="
        overflow-hidden
        rounded-[1.75rem]

        border
        border-slate-200
        border-t-4
        border-t-[#013E8E]

        bg-white
        shadow-sm

        dark:border-slate-800
        dark:border-t-blue-500
        dark:bg-slate-900
      "
    >
      {/* Header */}

      <div
        className="
          border-b
          border-slate-100

          px-4
          py-5

          dark:border-slate-800

          sm:px-6
        "
      >
        <div className="flex items-start gap-3">
          <span
            className="
              flex
              h-11 w-11
              shrink-0
              items-center
              justify-center

              rounded-2xl

              bg-blue-50

              text-lg
              text-[#013E8E]

              dark:bg-blue-500/10
              dark:text-blue-300
            "
          >
            <FaBookOpen />
          </span>

          <div className="min-w-0 flex-1">
            <p
              className="
                text-[10px]
                font-black
                uppercase
                tracking-[0.15em]

                text-[#013E8E]

                dark:text-blue-300
              "
            >
              À lire avant de commencer
            </p>

            <h2
              className="
                mt-1
                text-lg
                font-black

                text-slate-950

                dark:text-white

                sm:text-xl
              "
            >
              Informations et
              documents utiles
            </h2>

            <p
              className="
                mt-1
                max-w-3xl

                text-xs
                leading-5
                text-slate-500

                dark:text-slate-400

                sm:text-sm
              "
            >
              Retrouvez ici les
              consignes et documents
              nécessaires avant de
              compléter le dossier
              d'inscription.
            </p>
          </div>

          {totalFiles > 0 ? (
            <span
              className="
                hidden
                shrink-0

                rounded-full

                bg-slate-100

                px-3
                py-1.5

                text-xs
                font-bold
                text-slate-600

                dark:bg-slate-800
                dark:text-slate-300

                sm:inline-flex
              "
            >
              {totalFiles} document
              {totalFiles > 1
                ? "s"
                : ""}
            </span>
          ) : null}
        </div>
      </div>

      {/* Segments */}

      <div className="space-y-2 p-3 sm:p-4">
        {segments.map(
          (segment) => {
            const open =
              openId === segment.id;

            const files =
              Array.isArray(
                segment.fichiers,
              )
                ? segment.fichiers
                : [];

            return (
              <article
                key={segment.id}
                className="
                  overflow-hidden
                  rounded-2xl

                  border
                  border-slate-200

                  bg-white

                  dark:border-slate-800
                  dark:bg-slate-900
                "
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenId(
                      open
                        ? null
                        : segment.id,
                    )
                  }
                  className="
                    flex
                    w-full
                    items-center
                    gap-3

                    px-3
                    py-3.5

                    text-left

                    transition

                    hover:bg-blue-200

                    dark:hover:bg-slate-800/60

                    sm:px-4
                  "
                >
                  <span
                    className="
                      flex
                      h-9 w-9
                      shrink-0
                      items-center
                      justify-center

                      rounded-xl

                      bg-orange-400

                      text-white

                      dark:bg-orange-400
                      dark:text-white
                    "
                  >
                    <FaInfoCircle />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span
                      className="
                        block
                        font-bold

                        text-slate-900

                        dark:text-white
                      "
                    >
                      {
                        segment.segment_name
                      }
                    </span>

                    {files.length ? (
                      <span
                        className="
                          mt-0.5
                          block

                          text-[11px]
                          text-slate-400
                        "
                      >
                        {files.length} fichier
                        {files.length >
                        1
                          ? "s"
                          : ""}{" "}
                        disponible
                        {files.length >
                        1
                          ? "s"
                          : ""}
                      </span>
                    ) : null}
                  </span>

                  <FaChevronDown
                    className={`
                      shrink-0
                      text-slate-400
                      transition-transform

                      ${
                        open
                          ? "rotate-180"
                          : ""
                      }
                    `}
                  />
                </button>

                {open ? (
                  <div
                    className="
                      border-t
                      border-slate-100

                      px-4
                      py-4

                      dark:border-slate-800
                    "
                  >
                    <div
                      className="
                        prose
                        prose-sm
                        max-w-none

                        break-words

                        text-slate-600

                        prose-p:my-2
                        prose-a:text-[#013E8E]
                        prose-li:my-1

                        dark:prose-invert
                        dark:text-slate-300
                      "
                      dangerouslySetInnerHTML={{
                        __html:
                          segment.segment_content ||
                          "",
                      }}
                    />

                    {files.length ? (
                      <div
                        className="
                          mt-4
                          grid
                          gap-2

                          sm:grid-cols-2
                        "
                      >
                        {files.map(
                          (file) => (
                            <button
                              key={
                                file.id
                              }
                              type="button"
                              onClick={() =>
                                window.open(
                                  toAbsoluteUrl(
                                    file.lien_fichier,
                                  ),
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                              className="
                                flex
                                min-w-0
                                items-center
                                gap-3

                                rounded-xl
                                border
                                border-slate-200

                                bg-slate-50

                                px-3
                                py-3

                                text-left

                                transition

                                hover:border-blue-200
                                hover:bg-blue-50

                                dark:border-slate-700
                                dark:bg-slate-800/50
                                dark:hover:bg-blue-500/10
                              "
                            >
                              <span
                                className="
                                  flex
                                  h-9 w-9
                                  shrink-0
                                  items-center
                                  justify-center

                                  rounded-xl

                                  bg-white

                                  text-[#013E8E]

                                  shadow-sm

                                  dark:bg-slate-900
                                  dark:text-blue-300
                                "
                              >
                                <FaFileAlt />
                              </span>

                              <span className="min-w-0 flex-1">
                                <span
                                  className="
                                    block
                                    break-words

                                    text-xs
                                    font-bold

                                    text-slate-700

                                    dark:text-slate-200
                                  "
                                >
                                  {
                                    file.nom_fichier
                                  }
                                </span>

                                {file.detail_fichier ? (
                                  <span
                                    className="
                                      mt-0.5
                                      block

                                      text-[10px]
                                      text-slate-400
                                    "
                                  >
                                    {
                                      file.detail_fichier
                                    }
                                  </span>
                                ) : null}
                              </span>

                              <FaDownload className="shrink-0 text-slate-400" />
                            </button>
                          ),
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          },
        )}
      </div>
    </section>
  );
}